import {
  FETCH_CUSTOM_REPORTS_LIST,
  FETCH_CUSTOM_REPORTS_LIST_SUCCESS,
  FETCH_CUSTOM_REPORTS_LIST_FAILURE,
  SELECT_CUSTOM_REPORT,
  FETCH_CUSTOM_REPORTS_PARAMS,
  FETCH_CUSTOM_REPORTS_PARAMS_SUCCESS,
  FETCH_CUSTOM_REPORTS_PARAMS_FAILURE,
  RUN_CUSTOM_REPORT,
  RUN_CUSTOM_REPORT_SUCCESS,
  RUN_CUSTOM_REPORT_FAILURE,
  FETCH_CUSTOM_REPORT_SET,
  FETCH_CUSTOM_REPORT_SET_SUCCESS,
  FETCH_CUSTOM_REPORT_SET_FAILURE,
  RESET_CUSTOM_REPORT,
  UPDATE_QUERY_PARAMS,
  VERIFY_PARAM_OPTIONS_QUERY,
  VERIFY_PARAM_OPTIONS_QUERY_SUCCESS,
  VERIFY_PARAM_OPTIONS_QUERY_FAILURE,
} from './types';

import { customReportSelector } from './selectors';

import ModalNames from '../modalNames';

import {
  fetchEnabledCustomReports as fetchEnabledCustomReportsApi,
  fetchCustomReportSet as fetchCustomReportSetApi,
  runCustomReport as runCustomReportApi,
  getCustomReportSetParams as getCustomReportSetParamsApi,
} from '../../../shared/customReports/api';
import getModalActions from '../../../shared/modals/getModalActions';

import {
  fetchCustomReportsFactory,
  runCustomReportFactory,
} from '../../../shared/customReports/store/actions';

import { CustomReportTypes } from '../../../shared/customReports/constants';

import { fetchingError } from '../../../shared/store/actions';
import { errorNotification } from '../../../shared/store/notificationActions';

export const fetchCustomReports = fetchCustomReportsFactory({
  fetchApi: fetchEnabledCustomReportsApi,
  action: FETCH_CUSTOM_REPORTS_LIST,
  actionSuccess: FETCH_CUSTOM_REPORTS_LIST_SUCCESS,
  actionFailure: FETCH_CUSTOM_REPORTS_LIST_FAILURE,
});

export const selectCustomReport = customReportId => async (dispatch, getState) => {
  const state = getState();

  const updatedState = {
    reports: {
      customReports: {
        customReports: state.reports.customReports.customReports,
        customReportToViewId: customReportId,
      },
    },
  };

  const selectedCustomReport = customReportSelector(updatedState);

  if (selectedCustomReport.customReportType === CustomReportTypes.REPORT) {
    return dispatch({
      type: SELECT_CUSTOM_REPORT,
      customReportId,
    });
  }

  dispatch({
    type: FETCH_CUSTOM_REPORT_SET,
    customReportId,
  });

  try {
    const customReports = await fetchCustomReportSetApi(customReportId);

    return dispatch({
      type: FETCH_CUSTOM_REPORT_SET_SUCCESS,
      customReports,
    });
  } catch (error) {
    dispatch({
      type: FETCH_CUSTOM_REPORT_SET_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const fetchCustomReportParams = ({ reportSetId, paramToUniqueIdMapping, paramValues }) => async dispatch => {
  dispatch({
    type: FETCH_CUSTOM_REPORTS_PARAMS,
  });

  try {
    const params = await getCustomReportSetParamsApi({ reportSetId, paramValues });

    const mappedParams = params.map(param => {
      const name = paramToUniqueIdMapping
        && paramToUniqueIdMapping[param.reportSetId]
        && paramToUniqueIdMapping[param.reportSetId][param.name]
        ? paramToUniqueIdMapping[param.reportSetId][param.name]
        : param.name;

      return {
        name,
        options: param.rows.map(row => ({
          value: row[param.value],
          label: row[param.label],
        })),
      };
    });

    const result = mappedParams.reduce((result, param) => {
      if (param) {
        result[param.name] = param.options;
      }
      return result;
    }, {});

    dispatch({
      type: FETCH_CUSTOM_REPORTS_PARAMS_SUCCESS,
      params: result,
    });
  } catch (error) {
    dispatch({
      type: FETCH_CUSTOM_REPORTS_PARAMS_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

const runReport = async ({ customReportType, queries, reportId, params, apiCall }) => {
  if (customReportType === CustomReportTypes.REPORT_SET) {
    const reports = await Promise.all(queries.map(async query => {
      const result = await apiCall({ reportId: query.reportId, params: query.params });
      return { name: query.name, ...result };
    }));
    return { reports };
  }
  return await apiCall({ reportId, params });
};

export const runCustomReport = ({
  reportId,
  params = {},
  name,
  customReportType = CustomReportTypes.REPORT,
  queries = [],
}) => async dispatch => {
  dispatch({
    type: RUN_CUSTOM_REPORT,
  });

  try {
    const response = await runReport({
      customReportType,
      queries,
      reportId,
      params,
      apiCall: runCustomReportApi,
    });

    const result = {
      name,
      customReportType,
      ...response,
      ...(response.reports
        ? { reports: response.reports.map((report, index) => ({ ...report, pillName: queries[index].pillName })) }
        : {}
      ),
    };

    dispatch({
      type: RUN_CUSTOM_REPORT_SUCCESS,
      response: result,
    });
    return response;
  } catch (error) {
    dispatch({
      type: RUN_CUSTOM_REPORT_FAILURE,
    });
    dispatch(errorNotification(error.message));
  }
};

export const resetCustomReport = () => dispatch => {
  dispatch({
    type: RESET_CUSTOM_REPORT,
  });
};

export const updateCustomReportQueryParams = queryParams => dispatch => {
  dispatch({
    type: UPDATE_QUERY_PARAMS,
    queryParams,
  });
};

const {
  showAction: showResetCustomReportModal,
  hideAction: hideResetCustomReportModal,
} = getModalActions(ModalNames.ResetCustomReport);

export {
  showResetCustomReportModal,
  hideResetCustomReportModal,
};

export const verifyParamOptionsQuery = runCustomReportFactory({
  action: VERIFY_PARAM_OPTIONS_QUERY,
  actionSuccess: VERIFY_PARAM_OPTIONS_QUERY_SUCCESS,
  actionFailure: VERIFY_PARAM_OPTIONS_QUERY_FAILURE,
  responseTransform: response => {
    if (response.columns && response.columns.includes('validationResult')) {
      return response.rows;
    } else if (response.columns && response.columns.includes('validation_result')) {
      return response.rows;
    } else if (response.columns && response.columns.includes('VALIDATION_RESULT')) {
      return response.rows;
    } else if (response.columns) {
      return response.columns.map(column => ({
        value: column,
        label: column,
      }));
    } else {
      return response;
    }
  },
  successNotificationText: null,
});
